// frontend/src/hooks/useQuickBookings.ts
// React Query hooks for quick booking service operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  quickBookingService,
  ServiceDetails,
  QuickBookingRequest,
  QuickBookingResponse,
  ProviderQuote,
  BookingConfirmation
} from '@/lib/api/quickBookingService';
import { tokenManager } from '@/lib/httpClient';

// Query keys for quick bookings
export const quickBookingKeys = {
  all: ['quick-bookings'] as const,
  services: () => [...quickBookingKeys.all, 'services'] as const,
  servicesByLocation: (location: string) => [...quickBookingKeys.services(), location] as const,
  servicesByCategory: (categoryId: number) => [...quickBookingKeys.services(), 'category', categoryId] as const,
  providers: () => [...quickBookingKeys.all, 'providers'] as const,
  providersByService: (serviceId: number, location: string) => [...quickBookingKeys.providers(), serviceId, location] as const,
  quotes: () => [...quickBookingKeys.all, 'quotes'] as const,
  quote: (quoteId: string) => [...quickBookingKeys.quotes(), quoteId] as const,
  requests: () => [...quickBookingKeys.all, 'requests'] as const,
  request: (requestId: string) => [...quickBookingKeys.requests(), requestId] as const,
  availability: () => [...quickBookingKeys.all, 'availability'] as const,
  pricing: () => [...quickBookingKeys.all, 'pricing'] as const,
} as const;

// Get service by code
export const useGetServiceByCode = (serviceCode: string) => {
  return useQuery({
    queryKey: [...quickBookingKeys.services(), 'by-code', serviceCode],
    queryFn: async () => {
      const response = await quickBookingService.getServiceByCode(serviceCode);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!serviceCode,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 404) {
        return false; // Don't retry for not found
      }
      return failureCount < 3;
    },
  });
};

// Get available services for quick booking
export const useQuickBookingServices = (location?: string, categoryId?: number) => {
  return useQuery({
    queryKey: location 
      ? quickBookingKeys.servicesByLocation(location)
      : categoryId 
        ? quickBookingKeys.servicesByCategory(categoryId)
        : quickBookingKeys.services(),
    queryFn: async () => {
      const response = await quickBookingService.getAvailableServices(location, categoryId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get service details for quick booking
export const useQuickBookingServiceDetails = (serviceId: number) => {
  return useQuery({
    queryKey: [...quickBookingKeys.services(), 'detail', serviceId],
    queryFn: async () => {
      const response = await quickBookingService.getServiceDetails(serviceId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!serviceId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get available providers for a service and location
export const useAvailableProviders = (serviceId: number, location: string, filters: any = {}) => {
  return useQuery({
    queryKey: [...quickBookingKeys.providersByService(serviceId, location), filters],
    queryFn: async () => {
      const response = await quickBookingService.getAvailableProviders(serviceId, location, filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!serviceId && !!location,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Check service availability
export const useCheckAvailability = () => {
  return useMutation({
    mutationFn: async ({ 
      serviceId, 
      location, 
      dateTime, 
      duration 
    }: { 
      serviceId: number; 
      location: string; 
      dateTime: string; 
      duration?: number; 
    }) => {
      const response = await quickBookingService.checkAvailability(serviceId, location, dateTime, duration);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// Get instant pricing estimate
export const useGetPricingEstimate = () => {
  return useMutation({
    mutationFn: async ({ 
      serviceId, 
      location, 
      requirements 
    }: { 
      serviceId: number; 
      location: string; 
      requirements?: any; 
    }) => {
      const response = await quickBookingService.getPricingEstimate(serviceId, location, requirements);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// Submit quick booking request
export const useSubmitQuickBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingData: QuickBookingRequest) => {
      const response = await quickBookingService.submitQuickBooking(bookingData);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (newRequest) => {
      // Invalidate requests list
      queryClient.invalidateQueries({ queryKey: quickBookingKeys.requests() });
      
      // Add new request to cache if we have the data
      if (newRequest?.request) {
        queryClient.setQueryData(
          quickBookingKeys.request(newRequest.request.request_id),
          newRequest.request
        );
      }
    },
  });
};

// Get quick booking request status
export const useQuickBookingRequest = (requestId: string) => {
  return useQuery({
    queryKey: quickBookingKeys.request(requestId),
    queryFn: async () => {
      const response = await quickBookingService.getBookingRequest(requestId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!requestId,
    staleTime: 30 * 1000, // 30 seconds (real-time updates needed)
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get user's quick booking requests
export const useUserQuickBookingRequests = (filters: any = {}) => {
  return useQuery({
    queryKey: [...quickBookingKeys.requests(), 'user', filters],
    queryFn: async () => {
      const response = await quickBookingService.getUserBookingRequests(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Cancel quick booking request
export const useCancelQuickBookingRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason?: string }) => {
      const response = await quickBookingService.cancelBookingRequest(requestId, reason);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update specific request in cache
      queryClient.invalidateQueries({ 
        queryKey: quickBookingKeys.request(variables.requestId) 
      });
      
      // Invalidate requests list
      queryClient.invalidateQueries({ queryKey: quickBookingKeys.requests() });
    },
  });
};

// Get provider quotes for a request
export const useProviderQuotes = (requestId: string) => {
  return useQuery({
    queryKey: [...quickBookingKeys.request(requestId), 'quotes'],
    queryFn: async () => {
      const response = await quickBookingService.getProviderQuotes(requestId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!requestId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 45 * 1000, // Refetch every 45 seconds
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Accept provider quote
export const useAcceptProviderQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quoteId, requestId }: { quoteId: string; requestId: string }) => {
      const response = await quickBookingService.acceptProviderQuote(quoteId);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update request and quotes
      queryClient.invalidateQueries({ 
        queryKey: quickBookingKeys.request(variables.requestId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...quickBookingKeys.request(variables.requestId), 'quotes'] 
      });
      
      // Invalidate requests list
      queryClient.invalidateQueries({ queryKey: quickBookingKeys.requests() });
    },
  });
};

// Decline provider quote
export const useDeclineProviderQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      quoteId, 
      requestId, 
      reason 
    }: { 
      quoteId: string; 
      requestId: string; 
      reason?: string; 
    }) => {
      const response = await quickBookingService.declineProviderQuote(quoteId, reason);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update quotes
      queryClient.invalidateQueries({ 
        queryKey: [...quickBookingKeys.request(variables.requestId), 'quotes'] 
      });
    },
  });
};

// Get instant booking options (for immediate services)
export const useInstantBookingOptions = (serviceId: number, location: string) => {
  return useQuery({
    queryKey: [...quickBookingKeys.availability(), 'instant', serviceId, location],
    queryFn: async () => {
      const response = await quickBookingService.getInstantBookingOptions(serviceId, location);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!serviceId && !!location,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create instant booking (for immediate services)
export const useCreateInstantBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingData: any) => {
      const response = await quickBookingService.createInstantBooking(bookingData);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: quickBookingKeys.requests() });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

// Search services by location and query
export const useSearchQuickBookingServices = (searchQuery: string, location?: string) => {
  return useQuery({
    queryKey: [...quickBookingKeys.services(), 'search', searchQuery, location],
    queryFn: async () => {
      const response = await quickBookingService.searchServices(searchQuery, location);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!searchQuery && searchQuery.length > 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get popular services in area
export const usePopularServices = (location: string, limit: number = 10) => {
  return useQuery({
    queryKey: [...quickBookingKeys.services(), 'popular', location, limit],
    queryFn: async () => {
      const response = await quickBookingService.getPopularServices(location, limit);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!location,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get service categories for quick booking
export const useQuickBookingCategories = () => {
  return useQuery({
    queryKey: [...quickBookingKeys.services(), 'categories'],
    queryFn: async () => {
      const response = await quickBookingService.getServiceCategories();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Track booking request progress
export const useTrackBookingProgress = (requestId: string) => {
  return useQuery({
    queryKey: [...quickBookingKeys.request(requestId), 'progress'],
    queryFn: async () => {
      const response = await quickBookingService.trackBookingProgress(requestId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!requestId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time tracking
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Rate and review quick booking experience
export const useRateQuickBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      requestId, 
      rating, 
      review, 
      providerId 
    }: { 
      requestId: string; 
      rating: number; 
      review?: string; 
      providerId?: number; 
    }) => {
      const response = await quickBookingService.rateBooking(requestId, rating, review, providerId);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update request to show review
      queryClient.invalidateQueries({ 
        queryKey: quickBookingKeys.request(variables.requestId) 
      });
    },
  });
};

// Get quick booking history with filters
export const useQuickBookingHistory = (filters: any = {}) => {
  return useQuery({
    queryKey: [...quickBookingKeys.requests(), 'history', filters],
    queryFn: async () => {
      const response = await quickBookingService.getBookingHistory(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// ✅ NEW: Get service details with full information
export const useGetServiceDetails = (serviceId: number) => {
  return useQuery({
    queryKey: [...quickBookingKeys.services(), 'details', serviceId],
    queryFn: async () => {
      const response = await quickBookingService.getServiceDetails(serviceId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch service details');
    },
    enabled: !!serviceId && serviceId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};